import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { IDLE, LOADING } from "@src/constants";
import { getFromServerTopic } from "@utility/slice";
import { SAVING } from "@constants";
import { address } from "@utility/locationUtil";

export const BROWSE_BUILDINGS_TAB = "Browse";
export const SELECTED_BUILDINGS_TAB = "Selected";

const SLICE = "portfolios";

const action = (name) => `${SLICE}/${name}`;

export const getFromServer = (action, contextOptionsFn, payloadFn) =>
  getFromServerTopic({ topic: SLICE, action, contextOptionsFn, payloadFn });

const getPortfolios = getFromServer("getPortfolios");

const getBuildings = createAsyncThunk(
  action("getBuildings"),
  async (payload, { getState }) => {
    const { lookupdata } = getState().persistedAppData;
    return lookupdata.buildings;
  },
);

export const initializePortfoliosPage = createAsyncThunk(
  action("initializePortfoliosPage"),
  async (arg, { dispatch }) =>
    Promise.all([dispatch(getPortfolios()), dispatch(getBuildings())]),
);

export const createPortfolio = getFromServer(
  "createPortfolio",
  async ({ payload }) => payload,
  async (payload) => payload,
);

export const deletePortfolio = getFromServer(
  "deletePortfolio",
  async ({ payload }) => payload,
  async (payload) => payload,
);

export const savePortfolio = getFromServer(
  "savePortfolio",
  async ({ payload }) => {
    return { id: payload.id };
  },
  async (payload, { getState }) => {
    const state = getState();
    return {
      ...state.portfolios.selectedPortfolio,
      ...payload,
    };
  },
);

const filterBuildings = (unfiltered, searchQuery) => {
  if (!searchQuery || searchQuery === "") return unfiltered;

  const searchStrings = searchQuery.trim().toLowerCase().split(" ");
  return unfiltered.filter((item) => {
    const itemAsString = `${item.name} ${item.customer_name} ${address(item)} ${
      item.city
    } ${item.state_abbr} ${item.zip}`.toLowerCase();

    for (const searchString of searchStrings) {
      if (itemAsString.indexOf(searchString) === -1) return false;
    }

    return true;
  });
};

export const slice = createSlice({
  name: SLICE,
  initialState: {
    buildings: [],
    portfolios: [],
    searchQuery: "",
    selectedPortfolio: undefined,
    selectedTab: BROWSE_BUILDINGS_TAB,
    status: LOADING,
    unfilteredBuildings: [],
  },
  reducers: {
    changeTab: (state, action) => {
      state.selectedTab = action.payload;
      let buildings = filterBuildings(
        state.unfilteredBuildings,
        state.searchQuery,
      );
      if (state.selectedTab === SELECTED_BUILDINGS_TAB) {
        const selectedIds = new Set(state.selectedPortfolio.buildings);
        buildings = buildings.filter((b) => selectedIds.has(b.id));
      }
      state.buildings = buildings;
    },
    performSearch: (state, action) => {
      state.searchQuery = action.payload || "";
      state.buildings = filterBuildings(
        state.unfilteredBuildings,
        state.searchQuery,
      );
    },
    selectPortfolio: (state, action) => {
      state.selectedPortfolio = state.portfolios.find(
        (it) => it.id === action.payload,
      );
    },
    updateSearchQuery: (state, action) => {
      state.searchQuery = action.payload || "";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPortfolios.fulfilled, (state, action) => {
        state.portfolios = action.payload.response;
      })
      .addCase(getBuildings.fulfilled, (state, action) => {
        state.unfilteredBuildings = action.payload;
        state.buildings = filterBuildings(
          state.unfilteredBuildings,
          state.searchQuery,
        );
      })
      .addCase(initializePortfoliosPage.pending, (state) => {
        state.status = LOADING;
      })
      .addCase(initializePortfoliosPage.fulfilled, (state) => {
        state.status = IDLE;
      })
      .addCase(createPortfolio.pending, (state) => {
        state.status = SAVING;
      })
      .addCase(createPortfolio.fulfilled, (state, action) => {
        state.portfolios = [...state.portfolios, action.payload.response];
        state.selectedPortfolio = state.portfolios[state.portfolios.length - 1];
        state.status = IDLE;
      })
      .addCase(deletePortfolio.pending, (state) => {
        state.status = SAVING;
      })
      .addCase(deletePortfolio.fulfilled, (state, action) => {
        state.portfolios = action.payload.response;
        state.selectedPortfolio = undefined;
        state.status = IDLE;
      })
      .addCase(savePortfolio.fulfilled, (state, action) => {
        state.portfolios = action.payload.response;
        if (state.selectedPortfolio) {
          state.selectedPortfolio = state.portfolios.find(
            (it) => it.id === state.selectedPortfolio.id,
          );
        }
        state.status = IDLE;
      });
  },
});

export const { changeTab, performSearch, selectPortfolio, updateSearchQuery } =
  slice.actions;

export const INITIAL_STATE = slice.getInitialState();

export default slice.reducer;
